import React from 'react';
import { ArrowLeft, FileText, Shield, AlertTriangle, CheckCircle, Scale } from 'lucide-react';
import { scrollToTop } from '../utils/scrollToTop';

interface TermsAndConditionsProps {
  onBack?: () => void;
}

export function TermsAndConditions({ onBack }: TermsAndConditionsProps) {
  const handleBack = () => {
    scrollToTop();
    if (onBack) return onBack();
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <div className="container mx-auto px-4 pt-8">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </button>
      </div>
      
      <div className="container mx-auto px-4 pb-16 max-w-4xl">
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <Scale className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-5xl font-bold mb-6 text-gray-900">
            Terms & Conditions
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Please read these terms carefully before using Snip Rush. By using our service, you agree to these terms.
          </p>
        </div>

        {/* Key Points */}
        <div className="bg-purple-600 rounded-2xl p-8 mb-12">
          <h2 className="text-2xl font-bold text-center mb-6">📋 Key Terms Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Free to Use</h3>
              <p className="text-base text-white">
                Snip Rush is completely free with no hidden costs or subscriptions.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Privacy First</h3>
              <p className="text-base text-white">
                All processing happens locally. Your content never leaves your device.
              </p>
            </div>
          </div>
        </div>

        {/* Terms Content */}
        <div className="space-y-8">
          <section className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
              <FileText className="w-6 h-6 text-blue-400" />
              1. Acceptance of Terms
            </h2>
            <div className="space-y-4">
              <p className="text-gray-600">
                By accessing and using Snip Rush ("the Service"), you accept and agree to be bound by the terms and provision of this agreement.
              </p>
              <p className="text-gray-600">
                If you do not agree to abide by the above, please do not use this service.
              </p>
            </div>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
              <Shield className="w-6 h-6 text-green-400" />
              2. Use License
            </h2>
            <div className="space-y-4">
              <p className="text-gray-600">
                Permission is granted to temporarily use Snip Rush for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                <li>modify or copy the materials</li>
                <li>use the materials for any commercial purpose or for any public display</li>
                <li>attempt to reverse engineer any software contained on the website</li>
                <li>remove any copyright or other proprietary notations from the materials</li>
              </ul>
            </div>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-orange-400" />
              3. Disclaimer
            </h2>
            <div className="space-y-4">
              <p className="text-gray-600">
                The materials on Snip Rush are provided on an 'as is' basis. Snip Rush makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.
              </p>
              <div className="bg-orange-500/20 rounded-lg p-4">
                <p className="text-orange-700 font-semibold mb-2">Important Notice:</p>
                <p className="text-base text-gray-600">
                  While we strive to provide reliable service, we cannot guarantee 100% uptime or that all features will work perfectly on all devices and browsers.
                </p>
              </div>
            </div>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
            <h2 className="text-2xl font-bold mb-6">4. Privacy and Data Processing</h2>
            <div className="space-y-4">
              <p className="text-gray-600">
                Snip Rush is designed with privacy as a core principle:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                <li>All video and audio processing happens locally in your browser</li>
                <li>Your files are never uploaded to our servers</li>
                <li>No personal data is collected or stored</li>
                <li>We only collect anonymous usage analytics (page views, not content)</li>
              </ul>
              <div className="bg-green-500/20 rounded-lg p-4">
                <p className="text-green-700 font-semibold mb-2">Your Content Stays Private:</p>
                <p className="text-base text-gray-600">
                  We have no access to your videos, images, captions, or any content you create using our tools.
                </p>
              </div>
            </div>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
            <h2 className="text-2xl font-bold mb-6">5. Limitations</h2>
            <div className="space-y-4">
              <p className="text-gray-600">
                In no event shall Snip Rush or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on Snip Rush, even if Snip Rush or a Snip Rush authorized representative has been notified orally or in writing of the possibility of such damage.
              </p>
              <div className="bg-blue-500/20 rounded-lg p-4">
                <p className="text-blue-700 font-semibold mb-2">Performance Limitations:</p>
                <p className="text-base text-gray-600">
                  Processing speed and capabilities depend on your device's hardware and browser. Large files may take longer to process or may not be supported on older devices.
                </p>
              </div>
            </div>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
            <h2 className="text-2xl font-bold mb-6">6. Accuracy of Materials</h2>
            <div className="space-y-4">
              <p className="text-gray-600">
                The materials appearing on Snip Rush could include technical, typographical, or photographic errors. Snip Rush does not warrant that any of the materials on its website are accurate, complete, or current.
              </p>
              <p className="text-gray-600">
                Snip Rush may make changes to the materials contained on its website at any time without notice. However, Snip Rush does not make any commitment to update the materials.
              </p>
            </div>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
            <h2 className="text-2xl font-bold mb-6">7. Links</h2>
            <div className="space-y-4">
              <p className="text-gray-600">
                Snip Rush has not reviewed all of the sites linked to our website and is not responsible for the contents of any such linked site. The inclusion of any link does not imply endorsement by Snip Rush of the site. Use of any such linked website is at the user's own risk.
              </p>
            </div>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
            <h2 className="text-2xl font-bold mb-6">8. Modifications</h2>
            <div className="space-y-4">
              <p className="text-gray-600">
                Snip Rush may revise these terms of service for its website at any time without notice. By using this website, you are agreeing to be bound by the then current version of these terms of service.
              </p>
            </div>
          </section>

          <section className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
            <h2 className="text-2xl font-bold mb-6">9. Governing Law</h2>
            <div className="space-y-4">
              <p className="text-gray-600">
                These terms and conditions are governed by and construed in accordance with the laws of the United States and you irrevocably submit to the exclusive jurisdiction of the courts in that state or location.
              </p>
            </div>
          </section>

          <section className="bg-blue-600 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Questions About These Terms?</h2>
            <p className="text-xl text-white mb-6">
              If you have any questions about these Terms & Conditions, please contact us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleBack}
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-xl hover:bg-gray-100 transition-colors"
              >
                Back to Snip Rush
              </button>
            </div>
          </section>
        </div>

        <div className="text-center mt-12 text-base text-gray-600">
          <p>Last updated: {new Date().toLocaleDateString()}</p>
          <p className="mt-2">
            These terms apply to all users of Snip Rush and our video editing tools.
          </p>
        </div>
      </div>
    </div>
  );
}
